import { Toast } from '@/components/Toast';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useCallback, useEffect, useMemo, useState } from 'react';

type Stav = {
  eshop: string;
  cursor: string | null;
  page: number;
  scan_started: string | null;
  last_run_at: number | null;
  last_error: string | null;
};

type Row = {
  source_eshop: string;
  source_guid: string;
  target_eshop: string;
  product_name: string | null;
  author: string | null;
  rating: number | null;
  review_date: string | null;
  status: string;
  note: string | null;
  updated_at: number;
};

type Data = {
  enabled: boolean;
  stav: Stav[];
  counts: { source_eshop: string; status: string; n: number }[];
  rows: Row[];
};

type RunResult = {
  loaded: number;
  copied: number;
  updated: number;
  waiting: number;
  skipped: number;
  errors: number;
  more: boolean;
  busy?: true;
};

const STATUSY: { id: string; label: string; cls: string }[] = [
  { id: 'done', label: 'Zkopírováno', cls: 'bg-success-light text-success' },
  { id: 'pending', label: 'Čeká na zpracování', cls: 'bg-primary-50 text-primary' },
  { id: 'waiting', label: 'Neschválená / skrytá', cls: 'bg-bg text-text-secondary' },
  { id: 'skipped', label: 'Produkt v druhém e-shopu není', cls: 'bg-warning-light text-text' },
  { id: 'error', label: 'Chyba', cls: 'bg-danger-light text-danger' },
  { id: 'kontrola', label: 'Zkontrolovat ručně', cls: 'bg-danger-light text-danger' },
];

const SMERY = [
  { source: 'v-mart-cz', label: 'CZ → SK' },
  { source: 'v-mart-sk', label: 'SK → CZ' },
];

function fmtTime(ms: number | null): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString('cs-CZ', {
    day: 'numeric',
    month: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('cs-CZ');
}

/**
 * Sync recenzí CZ ↔ SK — schválené recenze se kopírují k produktu se stejným
 * kódem do druhého e-shopu, se značkou původu u jména.
 */
export function RecenzeSyncPage() {
  const [data, setData] = useState<Data | null>(null);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [testCode, setTestCode] = useState('');
  const [last, setLast] = useState<RunResult | null>(null);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await apiFetch<Data>(`/api/recenze${filter ? `?status=${filter}` : ''}`));
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.message : 'Načtení selhalo', kind: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  const count = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of data?.counts ?? []) m.set(`${c.source_eshop}|${c.status}`, c.n);
    return m;
  }, [data]);

  async function setEnabled(enabled: boolean) {
    setBusy('enabled');
    try {
      await apiFetch('/api/recenze/nastaveni', {
        method: 'PUT',
        body: JSON.stringify({ enabled }),
      });
      setToast({ msg: enabled ? 'Sync zapnutý' : 'Sync vypnutý', kind: 'success' });
      await load();
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.message : 'Uložení selhalo', kind: 'error' });
    } finally {
      setBusy('');
    }
  }

  async function runTest() {
    if (!testCode.trim()) return;
    setBusy('test');
    try {
      const r = await apiFetch<RunResult>('/api/recenze/test', {
        method: 'POST',
        body: JSON.stringify({ code: testCode.trim() }),
      });
      if (r.busy) setToast({ msg: 'Sync zrovna běží, zkus to za chvíli', kind: 'error' });
      setLast(r);
      await load();
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.message : 'Zkouška selhala', kind: 'error' });
    } finally {
      setBusy('');
    }
  }

  // Ruční běh — opakuje dávky, dokud je co dělat (první průchod historií).
  async function runNow() {
    setBusy('run');
    const total: RunResult = {
      loaded: 0,
      copied: 0,
      updated: 0,
      waiting: 0,
      skipped: 0,
      errors: 0,
      more: false,
    };
    try {
      for (let i = 0; i < 200; i++) {
        const r = await apiFetch<RunResult>('/api/recenze/run', { method: 'POST' });
        if (r.busy) {
          setToast({ msg: 'Sync zrovna běží, zkus to za chvíli', kind: 'error' });
          break;
        }
        for (const k of ['loaded', 'copied', 'updated', 'waiting', 'skipped', 'errors'] as const) {
          total[k] += r[k];
        }
        total.more = r.more;
        setLast({ ...total });
        if (!r.more) break;
      }
      await load();
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.message : 'Běh selhal', kind: 'error' });
    } finally {
      setBusy('');
    }
  }

  async function retry() {
    setBusy('retry');
    try {
      const r = await apiFetch<{ count: number }>('/api/recenze/znovu', { method: 'POST' });
      setToast({ msg: `Ke zpracování vráceno ${r.count} recenzí`, kind: 'success' });
      await load();
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.message : 'Akce selhala', kind: 'error' });
    } finally {
      setBusy('');
    }
  }

  const btn =
    'rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text hover:bg-surface-hover cursor-pointer disabled:opacity-50 disabled:cursor-default';

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text">Sync recenzí CZ ↔ SK</h1>
        <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
          Schválené recenze se kopírují k produktu se stejným kódem do druhého e-shopu. Kopie má u
          jména značku původu, např. „Jana K. (V-Mart.cz)". Bez e-mailu a údajů o objednávce.
        </p>
      </div>

      {data && (
        <div className="grid gap-4 md:grid-cols-2 mb-6">
          <div className="bg-surface rounded-xl shadow-card p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-semibold text-text">Automatický sync (1× denně v noci)</div>
                <div className="text-xs text-text-muted mt-0.5">
                  Zapni až po zkoušce na jednom produktu.
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={data.enabled}
                disabled={busy !== ''}
                onClick={() => void setEnabled(!data.enabled)}
                className={`relative inline-flex items-center h-6 w-11 rounded-full transition-colors cursor-pointer shrink-0 ${data.enabled ? 'bg-primary' : 'bg-border'}`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 h-5 w-5 bg-white rounded-full shadow transition-transform ${data.enabled ? 'translate-x-5' : ''}`}
                />
                <span className="sr-only">{data.enabled ? 'Zapnuto' : 'Vypnuto'}</span>
              </button>
            </div>
            <div className="flex gap-2 mt-4 flex-wrap">
              <button
                type="button"
                className={btn}
                disabled={busy !== ''}
                onClick={() => void runNow()}
              >
                {busy === 'run' ? 'Běží…' : 'Spustit teď'}
              </button>
              <button
                type="button"
                className={btn}
                disabled={busy !== ''}
                onClick={() => void retry()}
              >
                Zkusit znovu přeskočené a chyby
              </button>
            </div>
            <div className="mt-3 text-xs text-text-muted space-y-0.5">
              {data.stav.map((s) => (
                <div key={s.eshop}>
                  {s.eshop}: poslední běh {fmtTime(s.last_run_at)}
                  {s.cursor ? '' : ` · první průchod historií (stránka ${s.page})`}
                  {s.last_error && <span className="text-danger"> · {s.last_error}</span>}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-surface rounded-xl shadow-card p-4">
            <div className="font-semibold text-text">Zkouška na jednom produktu</div>
            <div className="text-xs text-text-muted mt-0.5">
              Kód varianty (např. L1025/104) — zkopíruje recenze jen tohoto produktu, i s vypnutým
              syncem.
            </div>
            <div className="flex gap-2 mt-3">
              <input
                value={testCode}
                onChange={(e) => setTestCode(e.target.value)}
                placeholder="Kód produktu"
                className="flex-1 rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:border-primary"
              />
              <button
                type="button"
                className={btn}
                disabled={busy !== '' || !testCode.trim()}
                onClick={() => void runTest()}
              >
                {busy === 'test' ? 'Kopíruji…' : 'Vyzkoušet'}
              </button>
            </div>
            {last && (
              <div className="mt-3 text-sm text-text-secondary">
                Načteno {last.loaded} · zkopírováno {last.copied} · upraveno {last.updated} ·
                neschválené {last.waiting} · přeskočeno {last.skipped} ·{' '}
                <span className={last.errors ? 'text-danger font-semibold' : ''}>
                  chyby {last.errors}
                </span>
                {last.more && ' · ještě není hotovo'}
              </div>
            )}
          </div>
        </div>
      )}

      {data && (
        <div className="bg-surface rounded-xl shadow-card overflow-x-auto mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider font-bold text-text">
                <th className="px-4 py-3">Směr</th>
                {STATUSY.map((s) => (
                  <th key={s.id} className="px-3 py-3 text-right">
                    {s.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {SMERY.map((d) => (
                <tr key={d.source} className="border-b border-border-light">
                  <td className="px-4 py-3 font-semibold text-text">{d.label}</td>
                  {STATUSY.map((s) => (
                    <td
                      key={s.id}
                      className="px-3 py-3 text-right tabular-nums text-text-secondary"
                    >
                      {count.get(`${d.source}|${s.id}`) ?? 0}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        <label className="text-sm text-text-secondary" htmlFor="recenze-filter">
          Zobrazit
        </label>
        <select
          id="recenze-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:border-primary"
        >
          <option value="">Posledních 100</option>
          {STATUSY.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
        {loading && <span className="text-sm text-text-muted">Načítám…</span>}
      </div>

      {data && data.rows.length === 0 && !loading && (
        <p className="text-sm text-text-muted">Zatím žádné recenze.</p>
      )}

      {data && data.rows.length > 0 && (
        <div className="bg-surface rounded-xl shadow-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider font-bold text-text">
                <th className="px-4 py-3">Směr</th>
                <th className="px-3 py-3">Produkt</th>
                <th className="px-3 py-3">Autor</th>
                <th className="px-3 py-3 text-right">Hvězdičky</th>
                <th className="px-3 py-3">Datum recenze</th>
                <th className="px-3 py-3">Stav</th>
                <th className="px-4 py-3">Změna</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => {
                const st = STATUSY.find((s) => s.id === r.status);
                return (
                  <tr
                    key={`${r.source_eshop}-${r.source_guid}`}
                    className="border-b border-border-light"
                  >
                    <td className="px-4 py-2 whitespace-nowrap text-text">
                      {r.source_eshop === 'v-mart-sk' ? 'SK → CZ' : 'CZ → SK'}
                    </td>
                    <td className="px-3 py-2 text-text">{r.product_name ?? '—'}</td>
                    <td className="px-3 py-2 text-text-secondary">{r.author || '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-text-secondary">
                      {r.rating ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-text-secondary whitespace-nowrap">
                      {fmtDate(r.review_date)}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${st?.cls ?? ''}`}
                      >
                        {st?.label ?? r.status}
                      </span>
                      {r.note && <div className="text-xs text-text-muted mt-0.5">{r.note}</div>}
                    </td>
                    <td className="px-4 py-2 text-text-muted whitespace-nowrap">
                      {fmtTime(r.updated_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {toast && <Toast message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />}
    </>
  );
}
